// frontend/src/pages/ManageStyles.jsx
// ✅ UPDATED: Model Trainer terminology + Future Feature badge + No animated progress bar

import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { getIdentityHeaders, waitForIdentity } from '../lib/identityGoogle';
import { API_BASE, normalizeListResponse, toast } from "../lib/api";
import PerformanceDashboard from "../components/PerformanceDashboard.jsx";

const STYLE_IMAGE_MAP = {
  'flare-form': 'flare-form.jpg',
  'gloom-pop': 'gloom-pop.jpg',
  'glow-morphic': 'glow-morphic.jpg',
  'neonkra': 'neonkra1.jpg',
  'ragga-puppets': 'ragga-puppets1.jpg',
};

function getStyleImagePath(styleId) {
  const filename = STYLE_IMAGE_MAP[styleId] || `${styleId}.jpg`;
  return `/assets/styles/${filename}`;
}

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralText: "#111827",
  neutralBorder: "#E5E7EB",
  shadow: "0 10px 24px rgba(17,24,39,.08)",
};

function normStyle(s) {
  const id = s?.id || s?._id || s?.slug || "";
  return {
    id,
    name: s?.name || s?.slug || (id || "Untitled"),
    status: s?.status || "trained",
  };
}

export default function ManageStyles() {
  const [loading, setLoading] = React.useState(true);
  const [stylesList, setStylesList] = React.useState([]);
  const [selectedStyle, setSelectedStyle] = React.useState("");

  const [files, setFiles] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadMsg, setUploadMsg] = React.useState("");
  const [trainingJobId, setTrainingJobId] = React.useState(null);

  // Info tooltip state
  const [showInfo, setShowInfo] = React.useState(false);

  // Performance toggle
  const [perfOpen, setPerfOpen] = React.useState(() => {
    try { return localStorage.getItem("ms_perf_open") === "1"; } catch { return false; }
  });

  // Reports state
  const [reportsOpen, setReportsOpen] = React.useState(() => {
    try { return localStorage.getItem("ms_reports_open") === "1"; } catch { return false; }
  });
  const [reportsLoading, setReportsLoading] = React.useState(false);
  const [reports, setReports] = React.useState([]);
  const [reportFilter, setReportFilter] = React.useState("all");

  // Model Trainer state (Future Premium Feature)
  const [modelTrainerOpen, setModelTrainerOpen] = React.useState(() => {
    try { return localStorage.getItem("ms_trainer_open") === "1"; } catch { return false; }
  });
  const [modelTrainerLoading, setModelTrainerLoading] = React.useState(false);
  const [modelTrainerHistory, setModelTrainerHistory] = React.useState([]);

  // Style Settings state
  const [selectedStyleForSettings, setSelectedStyleForSettings] = React.useState(null);
  const [selectedStyleName, setSelectedStyleName] = React.useState("");
  const [styleSettings, setStyleSettings] = React.useState(null);
  const [settingsLoading, setSettingsLoading] = React.useState(false);
  const [testPrompt, setTestPrompt] = React.useState("a beautiful landscape");
  const [testingGeneration, setTestingGeneration] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);

  // Tips collapsible state
  const [tipsOpen, setTipsOpen] = React.useState(() => {
    try { return localStorage.getItem("ms_tips_open") === "1"; } catch { return true; }
  });

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        await waitForIdentity();

        const res = await fetch(api("/api/creator/styles"), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });

        if (!res.ok) throw new Error("Failed to load styles");

        const json = await res.json();
        const styles = (Array.isArray(json) ? json : normalizeListResponse(json).items)
          .map(normStyle)
          .filter((st) => st.id && st.id !== "default");

        if (alive) {
          setStylesList(styles);
          if (!selectedStyle && styles.length) {
            setSelectedStyle(styles[0].id);
          }
        }
      } catch {
        toast("Could not load your styles.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [selectedStyle]);

  function onPickFiles(ev) {
    addFiles(Array.from(ev.target.files || []));
  }

  function onDrop(ev) {
    ev.preventDefault();
    addFiles(Array.from(ev.dataTransfer.files || []));
  }

  function addFiles(list) {
    const imgs = list.filter((f) => /^image\//i.test(f.type));
    setFiles((prev) => [...prev, ...imgs].slice(0, 20));
    setUploadMsg("");
    setTrainingJobId(null);
  }

  function removeAt(i) {
    setFiles((f) => f.filter((_, idx) => idx !== i));
  }

  function togglePerf() {
    const next = !perfOpen;
    setPerfOpen(next);
    try { localStorage.setItem("ms_perf_open", next ? "1" : "0"); } catch {}
  }

  function toggleReports() {
    const next = !reportsOpen;
    setReportsOpen(next);
    try { localStorage.setItem("ms_reports_open", next ? "1" : "0"); } catch {}
    
    if (next && reports.length === 0) {
      loadReports();
    }
  }

  function toggleModelTrainer() {
    const next = !modelTrainerOpen;
    setModelTrainerOpen(next);
    try { localStorage.setItem("ms_trainer_open", next ? "1" : "0"); } catch {}
    
    if (next && modelTrainerHistory.length === 0) {
      loadModelTrainerHistory();
    }
  }

  function toggleTips() {
    const next = !tipsOpen;
    setTipsOpen(next);
    try { localStorage.setItem("ms_tips_open", next ? "1" : "0"); } catch {}
  }

  async function loadReports() {
    setReportsLoading(true);
    try {
      await waitForIdentity();
      
      const res = await fetch(
        api(`/api/feedback/my-styles/reports?status=${reportFilter}&limit=100`),
        {
          credentials: "include",
          headers: getIdentityHeaders(),
        }
      );
      
      if (!res.ok) throw new Error("Failed to load reports");
      
      const data = await res.json();
      setReports(data.reports || []);
    } catch (e) {
      console.error("Failed to load reports:", e);
      toast("Failed to load reports");
    } finally {
      setReportsLoading(false);
    }
  }

  async function loadModelTrainerHistory() {
    setModelTrainerLoading(true);
    try {
      await waitForIdentity();
      
      const res = await fetch(
        api('/api/creator/debug/tunings'),
        {
          credentials: "include",
          headers: getIdentityHeaders(),
        }
      );
      
      if (!res.ok) throw new Error("Failed to load model trainer history");
      
      const data = await res.json();
      
      if (!data.exists) {
        setModelTrainerHistory([]);
        return;
      }
      
      setModelTrainerHistory(data.userTunings || []);
    } catch (e) {
      console.error("Failed to load model trainer history:", e);
      toast("Failed to load model trainer history");
      setModelTrainerHistory([]);
    } finally {
      setModelTrainerLoading(false);
    }
  }

  async function loadStyleSettings(styleId) {
  setSettingsLoading(true);
  setTestResult(null);
  try {
    await waitForIdentity();
    
    const style = stylesList.find(s => s.id === styleId);
    const styleName = style?.name || styleId;
    
    const res = await fetch(
      api(`/api/creator/styles/${encodeURIComponent(styleId)}/settings`),
      {
        credentials: "include",
        headers: getIdentityHeaders(),
      }
    );
    
    if (!res.ok) throw new Error("Failed to load settings");
    
    const data = await res.json();
    
    // ✅ CRITICAL: Ensure settings object has ALL required properties
    const defaultSettings = {
      strength: 0.7,
      guidance: 12,
      colorIntensity: 0.8,
      detailLevel: 0.7,
    };
    
    const settingsWithDefaults = {
      ...data,
      settings: {
        ...defaultSettings,
        ...(data.settings || {}),
      },
      presets: data.presets || {},
    };
    
    setStyleSettings(settingsWithDefaults);
    setSelectedStyleForSettings(styleId);
    setSelectedStyleName(styleName);
  } catch (e) {
    console.error("Failed to load settings:", e);
    toast("Failed to load style settings");
  } finally {
    setSettingsLoading(false);
  }
}

  async function updateStyleSettings(newSettings) {
    try {
      await waitForIdentity();
      
      const res = await fetch(
        api(`/api/creator/styles/${encodeURIComponent(selectedStyleForSettings)}/settings`),
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            ...getIdentityHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newSettings),
        }
      );
      
      if (!res.ok) throw new Error("Failed to update settings");
      
      const data = await res.json();
      setStyleSettings(prev => ({
        ...prev,
        settings: data.settings,
      }));
      
      toast("✅ Settings updated!");
    } catch (e) {
      console.error("Failed to update settings:", e);
      toast("❌ Failed to update settings");
    }
  }

  async function applyPreset(presetName) {
    try {
      await waitForIdentity();
      
      const res = await fetch(
        api(`/api/creator/styles/${encodeURIComponent(selectedStyleForSettings)}/settings/preset`),
        {
          method: "POST",
          credentials: "include",
          headers: {
            ...getIdentityHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ preset: presetName }),
        }
      );
      
      if (!res.ok) throw new Error("Failed to apply preset");
      
      const data = await res.json();
      setStyleSettings(prev => ({
        ...prev,
        settings: data.settings,
      }));
      
      setTestResult(null);
      
      toast(`✅ "${presetName}" preset applied!`);
    } catch (e) {
      console.error("Failed to apply preset:", e);
      toast("❌ Failed to apply preset");
    }
  }

  function resetSettings() {
  if (!window.confirm("Reset sliders to default values?")) return;
  
  // Just reset UI state - no API call needed
  setStyleSettings(prev => ({
    ...prev,
    settings: {
      strength: 0.7,
      guidance: 12,
      colorIntensity: 0.8,
      detailLevel: 0.7,
    }
  }));
  
  setTestResult(null);
  
  toast("✅ Sliders reset to defaults!");
}

  async function generateTest() {
    if (!testPrompt || testPrompt.length < 3) {
      toast("Enter a prompt to test");
      return;
    }
    
    setTestingGeneration(true);
    setTestResult(null);
    
    try {
      await waitForIdentity();
      
      const res = await fetch(
        api(`/api/creator/styles/${encodeURIComponent(selectedStyleForSettings)}/test-generation`),
        {
          method: "POST",
          credentials: "include",
          headers: {
            ...getIdentityHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            prompt: testPrompt,
            settings: styleSettings.settings
          }),
        }
      );
      
      if (!res.ok) throw new Error("Test generation failed");
      
      const data = await res.json();
      
      setTestResult({
        imageUrl: data.imageUrl,
        generationId: data.generationId,
        cost: data.cost
      });
      
      toast(`✅ Test generated! (${data.cost} credits)`);
      
    } catch (e) {
      console.error("Test generation failed:", e);
      toast("❌ Test generation failed");
    } finally {
      setTestingGeneration(false);
    }
  }

  async function submitSettingsRequest() {
  if (!window.confirm(
    "Submit these settings as an update request?\n\n" +
    "The ArtiBytes team will review your request and may update the style recipe."
  )) return;
  
  try {
    await waitForIdentity();
    
    const res = await fetch(
      api('/api/creator/settings-requests'),
      {
        method: "POST",
        credentials: "include",
        headers: {
          ...getIdentityHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          styleId: selectedStyleForSettings,
          styleName: selectedStyleName,
          requestedSettings: styleSettings.settings,
          reason: "Creator optimization request",
        }),
      }
    );
    
    if (!res.ok) throw new Error("Failed to submit request");
    
    const data = await res.json();
    
    toast("✅ Settings update request submitted! ArtiBytes team will review.");
    
    // Close panel
    setSelectedStyleForSettings(null);
    setSelectedStyleName("");
    setStyleSettings(null);
    setTestResult(null);
    
  } catch (e) {
    console.error("Failed to submit request:", e);
    toast("❌ Failed to submit settings request");
  }
}

  function getSettingLabel(setting, value) {
    switch (setting) {
      case 'strength':
        if (value < 0.4) return "Light";
        if (value < 0.7) return "Medium";
        if (value < 0.9) return "Strong";
        return "Maximum";
        
      case 'guidance':
        if (value < 8) return "Flexible";
        if (value < 12) return "Balanced";
        if (value < 16) return "Strict";
        return "Very Strict";
        
      case 'colorIntensity':
        if (value < 0.4) return "Muted";
        if (value < 0.7) return "Natural";
        if (value < 0.9) return "Vibrant";
        return "Vivid";
        
      case 'detailLevel':
        if (value < 0.4) return "Abstract";
        if (value < 0.7) return "Stylized";
        if (value < 0.9) return "Detailed";
        return "High Detail";
        
      default:
        return String(value);
    }
  }

  React.useEffect(() => {
    if (reportsOpen) {
      loadReports();
    }
  }, [reportFilter, reportsOpen]);

  async function uploadAll() {
    if (!selectedStyle || !files.length) {
      toast("Select a style and add images first.");
      return;
    }

    if (files.length < 5) {
      toast("Please upload at least 5 images for better training results.");
      return;
    }

    setUploading(true);
    setUploadMsg("");
    setTrainingJobId(null);

    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("images", f, f.name));

      const res = await fetch(
        api(`/api/creator/styles/${encodeURIComponent(selectedStyle)}/images`),
        {
          method: "POST",
          body: fd,
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        }
      );

      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch { json = null; }

      if (!res.ok) {
        const err = json?.error || text || "Upload failed";
        throw new Error(err);
      }

      const jobId = json?.tuningJobId;
      const imageCount = json?.imageCount || files.length;
      const message = json?.message || "Images uploaded successfully. Model training will improve your style.";

      setFiles([]);
      setUploadMsg(message);
      setTrainingJobId(jobId);
      
      toast(`✅ ${imageCount} images uploaded successfully!`);
      
      if (modelTrainerOpen) {
        setTimeout(() => loadModelTrainerHistory(), 500);
      }
    } catch (e) {
      const errorMsg = e.message || "Upload failed.";
      toast(`❌ ${errorMsg}`);
      setUploadMsg("");
    } finally {
      setUploading(false);
    }
  }

  return (
    <Screen>
      <StyleTag />

      {/* Hero */}
      <section className="ms-hero">
        <h1 
          className="h1"
          style={{
            background: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Manage Styles
        </h1>
        <div className="ms-hero__subtitle-wrapper">
          <p className="ms-hero__subtitle">
            Optimize performance • Adjust Settings • Resolve Issues
          </p>
          <p className="ms-hero__tips">
            💡 Toggle views for performance, reports, and style settings
          </p>
        </div>
      </section>

      {/* Local nav */}
      <section className="ms-local-nav">
        <Link to="/profile" className="btn">← Profile</Link>
        <button className="btn" onClick={togglePerf}>
          {perfOpen ? "Hide" : "View"} performance
        </button>
        <button className="btn" onClick={toggleReports}>
          {reportsOpen ? "Hide" : "View"} reports
          {reports.length > 0 && !reportsOpen && (
            <span className="badge">{reports.filter(r => r.status === "pending").length || reports.length}</span>
          )}
        </button>
        <button className="btn" onClick={toggleModelTrainer}>
          {modelTrainerOpen ? "Hide" : "View"} Model Trainer
          <span className="feature-badge">Future</span>
          {modelTrainerHistory.length > 0 && !modelTrainerOpen && (
            <span className="badge">{modelTrainerHistory.length}</span>
          )}
        </button>
      </section>

      {/* Performance dashboard */}
      {perfOpen && (
        <PerformanceDashboard />
      )}

      {/* Reports section */}
      {reportsOpen && (
        <section className="ms-reports">
          <div className="ms-card">
            <div className="reports-head">
              <h2 className="h2">Style Reports</h2>
              <div className="reports-filters">
                <button
                  className={`filter-btn ${reportFilter === "all" ? "active" : ""}`}
                  onClick={() => setReportFilter("all")}
                >
                  All
                </button>
                <button
                  className={`filter-btn ${reportFilter === "pending" ? "active" : ""}`}
                  onClick={() => setReportFilter("pending")}
                >
                  Pending
                </button>
                <button
                  className={`filter-btn ${reportFilter === "resolved" ? "active" : ""}`}
                  onClick={() => setReportFilter("resolved")}
                >
                  Resolved
                </button>
              </div>
            </div>

            {reportsLoading ? (
              <div className="reports-loading">Loading reports...</div>
            ) : reports.length === 0 ? (
              <div className="reports-empty">
                {reportFilter === "all" 
                  ? "No reports yet. This is good news - users haven't reported any issues with your styles!"
                  : `No ${reportFilter} reports.`}
              </div>
            ) : (
              <div className="reports-list">
                {reports.map((report) => (
                  <div key={report.id} className="report-card">
                    <div className="report-header">
                      <div className="report-style-tag">{report.styleName}</div>
                      <div className={`report-status status-${report.status}`}>
                        {report.status}
                      </div>
                    </div>
                    
                    <div className="report-reason">
                      <strong>Issue:</strong> {report.reason}
                    </div>
                    
                    <div className="report-meta">
                      <span className="report-date">
                        {new Date(report.createdAt).toLocaleDateString()} at{" "}
                        {new Date(report.createdAt).toLocaleTimeString()}
                      </span>
                      {report.generationId && (
                        <>
                          <span className="report-gen-id">
                            Gen: {report.generationId.slice(0, 8)}...
                          </span>
                          <Link 
                            to={`/creator/styles/${encodeURIComponent(report.styleId)}?generation=${report.generationId}&report=${report.reportId}`}
                            className="view-gen-link"
                          >
                            👁 View generation
                          </Link>
                        </>
                      )}
                    </div>

                    {report.generationPrompt && (
                      <div className="report-gen-details">
                        <div className="gen-detail-row">
                          <strong>Prompt:</strong> {report.generationPrompt}
                        </div>
                        {report.aspectRatio && (
                          <div className="gen-detail-row">
                            <strong>Aspect Ratio:</strong> {report.aspectRatio}
                          </div>
                        )}
                      </div>
                    )}

                    {report.resolution && (
                      <div className={`report-resolution resolution-${report.resolution}`}>
                        {report.resolution === "refund" ? (
                          <>✓ Refunded 1 credit to user</>
                        ) : (
                          <>✓ Dismissed as invalid</>
                        )}
                        {report.resolvedAt && (
                          <span className="resolution-date">
                            {" "}• {new Date(report.resolvedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Model Trainer section */}
      {modelTrainerOpen && (
        <section className="ms-model-trainer">
          <div className="ms-card">
            <div className="trainer-head">
              <div className="trainer-title-wrapper">
                <h2 className="h2">🤖 AI Model Trainer</h2>
                <span className="premium-badge">Premium Feature</span>
              </div>
              <button 
                className="btn-refresh"
                onClick={loadModelTrainerHistory}
                disabled={modelTrainerLoading}
              >
                Refresh
              </button>
            </div>

            <div className="trainer-demo-notice">
              <div className="demo-text">
                <strong>Demo Version</strong>This is a preview of our upcoming premium feature. 
                Full model training (10+ images, 8+ hours, $$+ per job) will be available in the future.
              </div>
            </div>

            {modelTrainerLoading ? (
              <div className="trainer-loading">Loading training history...</div>
            ) : modelTrainerHistory.length === 0 ? (
              <div className="trainer-empty">
                No training jobs yet. When available, upload images to train custom models and they'll appear here!
              </div>
            ) : (
              <div className="trainer-list">
                {modelTrainerHistory.map((job) => (
                  <div key={job.id} className="trainer-card">
                    <div className="trainer-header">
                      <div className="trainer-style-name">{job.styleName}</div>
                      <div className={`trainer-status status-${job.status}`}>
                        {job.status}
                      </div>
                    </div>

                    <div className="trainer-details">
                      <div className="trainer-detail-row">
                        <span className="trainer-label">Job ID:</span>
                        <span className="trainer-job-id">{job.id}</span>
                      </div>
                      
                      <div className="trainer-detail-row">
                        <span className="trainer-label">Style:</span>
                        <span className="trainer-value">{job.styleId}</span>
                      </div>

                      <div className="trainer-detail-row">
                        <span className="trainer-label">Images:</span>
                        <span className="trainer-value">{job.imageCount} uploaded</span>
                      </div>

                      <div className="trainer-detail-row">
                        <span className="trainer-label">Type:</span>
                        <span className="trainer-value">
                          {job.isBuiltIn ? "Built-in Style" : "Custom Style"}
                        </span>
                      </div>

                      <div className="trainer-detail-row">
                        <span className="trainer-label">Created:</span>
                        <span className="trainer-value">
                          {new Date(job.createdAt).toLocaleDateString()} at{" "}
                          {new Date(job.createdAt).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>

                    {job.status === "pending" && (
                      <div className="trainer-status-text">
                        ⏳ Queued for processing (demo mode)
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Style Settings Section - SPLIT LAYOUT */}
{selectedStyleForSettings && styleSettings && styleSettings.settings ? (
  <section className="ms-settings-section">
          <div className="ms-card">
            <div className="settings-header">
              <div>
                <h2 className="h2">Style Settings</h2>
                <p className="settings-subtitle">
                  Adjusting: <strong className="style-name-display">{selectedStyleName}</strong>
                </p>
              </div>
              <button 
                className="btn-close"
                onClick={() => {
                  setSelectedStyleForSettings(null);
                  setSelectedStyleName("");
                  setStyleSettings(null);
                  setTestResult(null);
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* SPLIT LAYOUT */}
            <div className="settings-split">
              {/* LEFT SIDE - Sliders */}
              <div className="settings-left">
                {/* Presets */}
                <div className="presets-section">
                  <h3 className="preset-title">Quick Presets</h3>
                  <div className="preset-grid">
                    {Object.entries(styleSettings.presets).map(([key, preset]) => (
                      <button
                        key={key}
                        className="preset-btn"
                        onClick={() => applyPreset(key)}
                      >
                        <div className="preset-name">{preset.name}</div>
                        <div className="preset-desc">{preset.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Settings Sliders */}
                <div className="settings-controls">
                  {/* Style Strength */}
                  <div className="setting-item">
                    <div className="setting-header">
                      <label className="setting-label">Style Strength</label>
                      <span className="setting-value">
                        {styleSettings.settings.strength.toFixed(2)} - {getSettingLabel('strength', styleSettings.settings.strength)}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.1"
                      max="1.0"
                      step="0.05"
                      value={styleSettings.settings.strength}
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value);
                        const newSettings = { ...styleSettings.settings, strength: newValue };
                        setStyleSettings(prev => ({ ...prev, settings: newSettings }));
                        updateStyleSettings({ strength: newValue });
                      }}
                      className="setting-slider"
                    />
                    <p className="setting-hint">
                      How strongly your style is applied to generations
                    </p>
                  </div>

                  {/* Guidance Scale */}
                  <div className="setting-item">
                    <div className="setting-header">
                      <label className="setting-label">Guidance Scale</label>
                      <span className="setting-value">
                        {styleSettings.settings.guidance} - {getSettingLabel('guidance', styleSettings.settings.guidance)}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      step="1"
                      value={styleSettings.settings.guidance}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        const newSettings = { ...styleSettings.settings, guidance: newValue };
                        setStyleSettings(prev => ({ ...prev, settings: newSettings }));
                        updateStyleSettings({ guidance: newValue });
                      }}
                      className="setting-slider"
                    />
                    <p className="setting-hint">
                      How strictly the style should be followed
                    </p>
                  </div>

                  {/* Color Intensity */}
                  <div className="setting-item">
                    <div className="setting-header">
                      <label className="setting-label">Color Intensity</label>
                      <span className="setting-value">
                        {styleSettings.settings.colorIntensity.toFixed(2)} - {getSettingLabel('colorIntensity', styleSettings.settings.colorIntensity)}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.1"
                      max="1.0"
                      step="0.05"
                      value={styleSettings.settings.colorIntensity}
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value);
                        const newSettings = { ...styleSettings.settings, colorIntensity: newValue };
                        setStyleSettings(prev => ({ ...prev, settings: newSettings }));
                        updateStyleSettings({ colorIntensity: newValue });
                      }}
                      className="setting-slider"
                    />
                    <p className="setting-hint">
                      Color vibrancy and saturation level
                    </p>
                  </div>

                  {/* Detail Level */}
                  <div className="setting-item">
                    <div className="setting-header">
                      <label className="setting-label">Detail Level</label>
                      <span className="setting-value">
                        {styleSettings.settings.detailLevel.toFixed(2)} - {getSettingLabel('detailLevel', styleSettings.settings.detailLevel)}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.1"
                      max="1.0"
                      step="0.05"
                      value={styleSettings.settings.detailLevel}
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value);
                        const newSettings = { ...styleSettings.settings, detailLevel: newValue };
                        setStyleSettings(prev => ({ ...prev, settings: newSettings }));
                        updateStyleSettings({ detailLevel: newValue });
                      }}
                      className="setting-slider"
                    />
                    <p className="setting-hint">
                      How much detail to preserve vs. stylize
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="settings-actions">
  <button className="btn-reset" onClick={resetSettings}>
    ↺ Reset Sliders
  </button>
  
  <div className="settings-info">
    <div className="info-icon">💡</div>
    <div className="info-text">
      <strong>Test changes, then submit for review</strong>
      Generate test images with different settings. When you find optimal settings, submit a request to update your style.
    </div>
  </div>
  
  <button 
    className="btn-done"
    onClick={submitSettingsRequest}
  >
     Submit Settings Update Request
  </button>
</div>
              </div>

              {/* RIGHT SIDE - Test Generation */}
              <div className="settings-right">
                <h3 className="test-title">Test your new settings</h3>
                
                {/* Guide Image */}
<div className="guide-image-wrapper">
  <div className="guide-image-label">Current Style Output</div>
  <div className="guide-image">
    <img 
      src={styleSettings.guideImageUrl || getStyleImagePath(selectedStyleForSettings)}
      alt={`${selectedStyleName} guide`}
      onError={(e) => {
        // Fallback to local path if URL fails
        e.target.src = getStyleImagePath(selectedStyleForSettings);
      }}
    />
  </div>
</div>

                {/* Test Result (if exists) */}
                {testResult && (
                  <div className="test-result-wrapper">
                    <div className="test-result-label">Your Test Result</div>
                    <div className="test-result-image">
                      <img src={testResult.imageUrl} alt="Test result" />
                    </div>
                    <p className="test-result-success">
                      ✨ Test generated! Like it? Click "Save New Settings"
                    </p>
                  </div>
                )}

                {/* Test Prompt */}
                <div className="test-prompt-section">
                  <label className="test-prompt-label">Add Prompt</label>
                  <input
                    type="text"
                    className="test-prompt-input"
                    value={testPrompt}
                    onChange={(e) => setTestPrompt(e.target.value)}
                    placeholder="e.g. sunset over mountains"
                  />
                </div>

                {/* Cost & Button */}
                <div className="test-actions">
                  <div className="test-cost"> 0.5 credits per test</div>
                  <button
                    className="btn-generate-test"
                    onClick={generateTest}
                    disabled={testingGeneration}
                  >
                    {testingGeneration ? (
                      <>⏳ Generating...</>
                    ) : testResult ? (
                      <> Generate Again</>
                    ) : (
                      <> Generate Test</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
      </section>
    ) : selectedStyleForSettings && settingsLoading ? (
      <section className="ms-settings-section">
        <div className="ms-card">
          <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280', fontSize: '16px' }}>
            ⏳ Loading settings...
          </div>
        </div>
      </section>
    ) : null}

      {/* Style selector for settings - OWNER ONLY */}

      {/* Style selector for settings - OWNER ONLY */}
{stylesList.length > 0 && (
  <section className="ms-style-selector">
    <div className="ms-card">
      <div className="selector-header">
        <h2 className="h2">Adjust Style Settings</h2>
        <p className="selector-subtitle">
          Fine-tune how your styles appear in generations (Owner Only)
        </p>
      </div>

      <label className="field">
        <span className="field__label">Select a style to adjust</span>
        <select
          className="select"
          value={selectedStyle}
          onChange={(e) => setSelectedStyle(e.target.value)}
          disabled={loading}
        >
          <option value="">
            {loading ? "Loading styles…" : stylesList.length ? "— Choose a style —" : "No styles available"}
          </option>
          {stylesList.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>

      {selectedStyle && (
        <div className="style-settings-trigger">
          <button
            className="btn-settings"
            onClick={() => loadStyleSettings(selectedStyle)}
            disabled={settingsLoading}
          >
            {settingsLoading ? "Loading..." : "⚙️ Adjust Style Settings"}
          </button>
        </div>
      )}
    </div>
  </section>
)}

    </Screen>
  );
}

function StyleTag() {
  return (
    <style>{`
.ms-hero{
  border:1px solid ${brand.purpleBorder};
  border-radius:16px;
  padding:18px;
  margin-bottom:18px;
  background: linear-gradient(180deg, ${brand.purpleTint}, #fff);
}

.ms-local-nav{
  display:flex; gap:10px; margin-bottom:18px; flex-wrap:wrap; align-items:center;
}

.feature-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:2px 8px;
  border-radius:999px;
  background:linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
  color:#fff;
  font-size:10px;
  font-weight:800;
  margin-left:8px;
  text-transform:uppercase;
  letter-spacing:0.05em;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
}

.ms-card{
  background:#fff;
  border:1px solid ${brand.neutralBorder};
  border-radius:16px;
  padding:18px;
  box-shadow:${brand.shadow};
  margin-bottom:18px;
}

.ms-style-selector{
  margin-bottom:18px;
}

.selector-header{
  margin-bottom:20px;
}

.selector-subtitle{
  font-size:14px;
  color:#6B7280;
  margin-top:6px;
}

.field{ display:grid; gap:6px; margin-bottom:16px; }
.field__label{ font-weight:800; font-size:14px; }

.select{
  height:44px;
  border-radius:12px;
  border:1px solid ${brand.neutralBorder};
  padding:0 12px;
  background:#fff;
  font-size:15px;
  cursor:pointer;
  transition: all 0.2s;
}
.select:hover{ border-color:${brand.purple}; }
.select:focus{ outline:none; border-color:${brand.purple}; box-shadow: 0 0 0 3px ${brand.purpleTint}; }

.style-settings-trigger{
  margin-top: 8px;
}

.ms-hero__subtitle-wrapper {
  margin-top: 12px;
}

.ms-hero__subtitle { 
  color: #6B7280; 
  font-size: 15px;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.ms-hero__tips {
  color: #9CA3AF;
  font-size: 13px;
  margin: 0;
  line-height: 1.5;
}

.reports-loading, .reports-empty{
  padding:20px; text-align:center; color:#6B7280; line-height:1.5;
}

.reports-head{
  display:flex; align-items:center; justify-content:space-between; 
  margin-bottom:14px; flex-wrap:wrap; gap:12px;
}

.reports-filters{
  display:flex; gap:8px; flex-wrap:wrap;
}

.filter-btn{
  height:32px;
  padding:0 12px;
  border-radius:8px;
  border:1px solid ${brand.neutralBorder};
  background:#fff;
  font-size:13px;
  font-weight:700;
  cursor:pointer;
  transition: all 0.2s;
}

.filter-btn:hover{
  border-color:${brand.purple};
  background:${brand.purpleTint};
}

.filter-btn.active{
  background:${brand.purple};
  color:#fff;
  border-color:${brand.purple};
}

.ms-reports{ margin-bottom:18px; }

.reports-list{
  display:grid; gap:12px;
}

.report-card{
  padding:14px;
  background:#F9FAFB;
  border:1px solid ${brand.neutralBorder};
  border-radius:12px;
  transition: all 0.2s;
}

.report-card:hover{
  border-color:${brand.purple};
  box-shadow: 0 2px 8px rgba(109, 94, 249, 0.1);
}

.report-header{
  display:flex; align-items:center; justify-content:space-between;
  margin-bottom:10px; gap:12px; flex-wrap:wrap;
}

.report-style-tag{
  background:${brand.purple};
  color:#fff;
  padding:4px 10px;
  border-radius:999px;
  font-size:12px;
  font-weight:800;
}

.report-status{
  padding:4px 10px;
  border-radius:999px;
  font-size:11px;
  font-weight:800;
  text-transform:uppercase;
  letter-spacing:0.05em;
}

.status-pending{
  background:#FEF3C7;
  color:#92400E;
  border:1px solid #FDE047;
}

.status-resolved{
  background:#D1FAE5;
  color:#065F46;
  border:1px solid #6EE7B7;
}

.report-reason{
  color:${brand.neutralText};
  font-size:14px;
  line-height:1.5;
  margin-bottom:10px;
}

.report-reason strong{
  color:#6B7280;
  font-weight:700;
}

.report-meta{
  display:flex; gap:12px; flex-wrap:wrap;
  font-size:12px; color:#6B7280;
}

.report-gen-id{
  font-family:monospace;
  background:#F3F4F6;
  padding:2px 6px;
  border-radius:4px;
}

.view-gen-link{
  display:inline-flex;
  align-items:center;
  gap:4px;
  color:${brand.purple};
  font-weight:700;
  font-size:12px;
  text-decoration:none;
  padding:4px 8px;
  background:${brand.purpleTint};
  border-radius:6px;
  transition: all 0.2s;
}

.view-gen-link:hover{
  background:${brand.purple};
  color:#fff;
  transform:translateY(-1px);
}

.report-resolution{
  margin-top:12px;
  padding:10px;
  border-radius:8px;
  font-size:13px;
  font-weight:700;
  display:flex;
  align-items:center;
  gap:8px;
}

.resolution-refund{
  background:#D1FAE5;
  color:#065F46;
  border:1px solid #6EE7B7;
}

.resolution-dismiss{
  background:#F3F4F6;
  color:#6B7280;
  border:1px solid #D1D5DB;
}

.resolution-date{
  font-weight:400;
  opacity:0.8;
}

.report-gen-details{
  margin-top:12px;
  padding-top:12px;
  border-top:1px solid ${brand.neutralBorder};
  display:grid;
  gap:8px;
}

.gen-detail-row{
  font-size:13px;
  line-height:1.5;
  color:${brand.neutralText};
}

.gen-detail-row strong{
  color:#6B7280;
  font-weight:700;
  margin-right:6px;
}

/* ===== MODEL TRAINER STYLES ===== */
.ms-model-trainer{ margin-bottom:18px; }

.trainer-head{
  display:flex; align-items:center; justify-content:space-between;
  margin-bottom:14px; flex-wrap:wrap; gap:12px;
}

.trainer-title-wrapper{
  display:flex; align-items:center; gap:12px;
  flex-wrap:wrap;
}

.premium-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:4px 12px;
  border-radius:999px;
  background:linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
  color:#fff;
  font-size:11px;
  font-weight:800;
  text-transform:uppercase;
  letter-spacing:0.05em;
  box-shadow: 0 2px 6px rgba(139, 92, 246, 0.3);
}

.trainer-demo-notice{
  display:flex;
  gap:12px;
  padding:16px;
  margin-bottom:20px;
  background:linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border:2px solid #F59E0B;
  border-radius:12px;
}

.demo-icon{
  font-size:28px;
  flex-shrink:0;
}

.demo-text{
  font-size:13px;
  color:#92400E;
  line-height:1.6;
}

.demo-text strong{
  font-weight:800;
  display:block;
  margin-bottom:4px;
  font-size:14px;
}

.btn-refresh{
  height:32px;
  padding:0 12px;
  border-radius:8px;
  border:1px solid ${brand.neutralBorder};
  background:#fff;
  font-size:13px;
  font-weight:700;
  cursor:pointer;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled){
  border-color:${brand.purple};
  background:${brand.purpleTint};
}

.btn-refresh:disabled{
  opacity:0.5;
  cursor:not-allowed;
}

.trainer-loading, .trainer-empty{
  padding:20px; text-align:center; color:#6B7280; line-height:1.5;
}

.trainer-list{
  display:grid; gap:12px;
}

.trainer-card{
  padding:16px;
  background:linear-gradient(135deg, #F0F9FF 0%, #FFF7ED 100%);
  border:1px solid ${brand.purpleBorder};
  border-radius:12px;
  transition: all 0.2s;
}

.trainer-card:hover{
  border-color:${brand.purple};
  box-shadow: 0 4px 12px rgba(109, 94, 249, 0.15);
  transform:translateY(-2px);
}

.trainer-header{
  display:flex; align-items:center; justify-content:space-between;
  margin-bottom:12px; gap:12px; flex-wrap:wrap;
}

.trainer-style-name{
  font-size:16px;
  font-weight:800;
  color:${brand.neutralText};
}

.trainer-status{
  padding:4px 10px;
  border-radius:999px;
  font-size:11px;
  font-weight:800;
  text-transform:uppercase;
  letter-spacing:0.05em;
}

.trainer-status.status-pending{
  background:#FEF3C7;
  color:#92400E;
  border:1px solid #FDE047;
}

.trainer-status.status-processing{
  background:#DBEAFE;
  color:#1E40AF;
  border:1px solid #93C5FD;
}

.trainer-status.status-completed{
  background:#D1FAE5;
  color:#065F46;
  border:1px solid #6EE7B7;
}

.trainer-status.status-failed{
  background:#FEE2E2;
  color:#991B1B;
  border:1px solid #FCA5A5;
}

.trainer-details{
  display:grid;
  gap:8px;
  padding:12px;
  background:rgba(255,255,255,0.6);
  border-radius:8px;
}

.trainer-detail-row{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  font-size:13px;
}

.trainer-label{
  color:#6B7280;
  font-weight:700;
}

.trainer-value{
  color:${brand.neutralText};
  font-weight:600;
}

.trainer-job-id{
  font-family:monospace;
  font-size:11px;
  background:#F3F4F6;
  padding:4px 8px;
  border-radius:4px;
  word-break:break-all;
}

.trainer-status-text{
  margin-top:12px;
  padding:10px;
  background:rgba(254, 243, 199, 0.5);
  border-radius:8px;
  font-size:12px;
  color:#92400E;
  text-align:center;
  font-weight:600;
}

/* ===== SPLIT LAYOUT STYLES ===== */

.ms-settings-section {
  margin-bottom: 18px;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
}

.settings-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 5px;
  font-weight: 500;
}

.style-name-display {
  color: ${brand.purple};
  font-weight: 800;
}

.btn-close {
  padding: 8px 16px;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #e5e7eb;
  color: #374151;
}

.settings-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.settings-left {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.settings-right {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 12px;
  border: 2px solid #e5e7eb;
}

.presets-section {
  padding: 20px;
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border-radius: 12px;
  border: 2px solid #FDE047;
}

.preset-title {
  font-size: 16px;
  font-weight: 800;
  color: #92400E;
  margin-bottom: 15px;
  margin-top: 0;
}

.preset-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

/* ✅ Ensure 2 columns even on small mobile screens */
@media (max-width: 400px) {
  .preset-grid {
    grid-template-columns: repeat(2, 1fr);  /* Force 2 columns */
    gap: 10px;  /* Slightly tighter on very small screens */
  }
  
  .preset-btn {
    padding: 12px 10px;  /* Slightly smaller padding on mobile */
  }
  
  .preset-name {
    font-size: 13px;  /* Slightly smaller text on mobile */
  }
  
  .preset-desc {
    font-size: 11px;  /* Slightly smaller description on mobile */
    line-height: 1.2;
  }
}

/* ✅ Additional mobile optimizations for medium screens */
@media (max-width: 600px) {
  .preset-btn {
    padding: 12px;
  }
  
  .preset-name {
    font-size: 13px;
  }
  
  .preset-desc {
    font-size: 11px;
    line-height: 1.3;
  }
  
  .presets-section {
    padding: 16px;  /* Slightly less padding on mobile */
  }
}

.preset-btn {
  padding: 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid transparent;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.preset-btn:hover {
  background: #ffffff;
  border-color: #F59E0B;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
}

.preset-name {
  font-size: 14px;
  font-weight: 800;
  color: #92400E;
  margin-bottom: 4px;
}

.preset-desc {
  font-size: 12px;
  color: #92400E;
  opacity: 0.8;
  line-height: 1.3;
}

.settings-controls {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.setting-item {
  padding: 18px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.setting-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.setting-label {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.setting-value {
  font-size: 13px;
  font-weight: 600;
  color: #6366f1;
  background: #eef2ff;
  padding: 4px 10px;
  border-radius: 6px;
}

.setting-slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  margin-bottom: 8px;
  cursor: pointer;
}

.setting-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #6366f1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
  transition: all 0.2s;
}

.setting-slider::-webkit-slider-thumb:hover {
  background: #4f46e5;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.6);
}

.setting-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #6366f1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
  transition: all 0.2s;
  border: none;
}

.setting-slider::-moz-range-thumb:hover {
  background: #4f46e5;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.6);
}

.setting-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  font-style: italic;
}

.settings-actions {
  display: flex;
  flex-direction: column;  /* ✅ Changed to column for vertical stacking */
  gap: 12px;
}

.btn-reset {
  width: 100%;  /* ✅ Full width in column layout */
  padding: 12px 24px;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-reset:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: #fef2f2;
}

.btn-done {
  width: 100%;  /* ✅ Full width in column layout */
  padding: 12px 32px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-done:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.settings-info {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #eff6ff;
  border: 2px solid #bfdbfe;
  border-radius: 10px;
  /* Margin removed - parent flex gap handles spacing */
}

.info-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.info-text {
  font-size: 13px;
  color: #1e40af;
  line-height: 1.5;
}

.info-text strong {
  font-weight: 700;
  display: block;
  margin-bottom: 4px;
}

.test-title {
  font-size: 18px;
  font-weight: 800;
  color: ${brand.neutralText};
  margin: 0 0 15px 0;
}

.guide-image-wrapper {
  margin-bottom: 20px;
}

.guide-image-label {
  font-size: 13px;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 8px;
}

.guide-image {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  background: #f9fafb;
}

.guide-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.no-guide-image {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}

.no-guide-image span {
  font-size: 48px;
  margin-bottom: 8px;
}

.no-guide-image p {
  font-size: 14px;
  margin: 0;
}

.test-result-wrapper {
  margin-bottom: 20px;
  padding-top: 20px;
  border-top: 2px dashed #e5e7eb;
}

.test-result-label {
  font-size: 13px;
  font-weight: 700;
  color: #059669;
  margin-bottom: 8px;
}

.test-result-image {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  margin-bottom: 12px;
}

.test-result-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.test-result-success {
  font-size: 13px;
  color: #059669;
  text-align: center;
  margin: 0;
  font-weight: 600;
  line-height: 1.4;
}

.test-prompt-section {
  margin-bottom: 15px;
}

.test-prompt-label {
  font-size: 13px;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 8px;
  display: block;
}

.test-prompt-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  background: #ffffff;
  color: #111827;
  transition: all 0.2s;
}

.test-prompt-input:focus {
  outline: none;
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.test-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.test-cost {
  text-align: center;
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
}

.btn-generate-test {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-generate-test:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.4);
}

.btn-generate-test:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 968px) {
  .settings-split {
    grid-template-columns: 1fr;
  }
  
  /* ✅ REMOVED: preset-grid override - keep it 2 columns on mobile! */
  
  .settings-actions {
    flex-direction: column;
  }
  
  .btn-reset,
  .btn-done {
    width: 100%;
  }
}

.btn-settings {
  width: 100%;
  padding: 12px 16px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-settings:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.btn-settings:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-width:20px;
  height:20px;
  padding:0 6px;
  border-radius:999px;
  background:#EF4444;
  color:#fff;
  font-size:11px;
  font-weight:800;
  margin-left:6px;
}

.btn{
  height:42px;
  padding:0 16px;
  border-radius:12px;
  border:1px solid ${brand.purpleBorder};
  background:${brand.purpleTint};
  color:${brand.neutralText};
  font-weight:700;
  cursor:pointer;
  transition: all 0.2s;
  text-decoration:none;
  display:inline-flex;
  align-items:center;
  justify-content:center;
}
.btn:hover{
  background: #E9DEFF;
  border-color: ${brand.purple};
}

.h1{ font-size:32px; font-weight:900; margin:0; }
.h2{ font-size:20px; font-weight:800; margin:0; }
    `}</style>
  );
}